import styled, { css } from 'styled-components';

import { colors, radius } from '../../../../styles/tokens';

export const Container = styled.div`
  padding: 40px;
  min-height: 100%;

  display: flex;
  flex-direction: column;
  gap: 32px;

  @media (max-width: 1199px) {
    padding: 20px;
  }
`;

export const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;

  @media (max-width: 1199px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
`;

export const PageTitle = styled.h1`
  font-size: 40px;
  font-weight: 800;
  color: ${colors.onSurface};
  letter-spacing: -0.02em;
  margin-bottom: 8px;
`;

export const PageSubtitle = styled.p`
  font-size: 14px;
  font-weight: 500;
  color: ${colors.onSurfaceVariant};
`;

export const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;

  padding: 14px 28px;

  background-color: ${colors.primary};
  border: none;
  border-radius: ${radius.full};
  cursor: pointer;

  color: ${colors.onPrimary};
  font-size: 12px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.1em;

  transition: box-shadow 0.2s;

  &:hover {
    box-shadow: 0 0 24px ${colors.primary}40;
  }
`;

export const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const ExportButton = styled.button<{ locked?: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;

  padding: 14px 24px;

  background-color: transparent;
  border: 1px solid ${colors.outline};
  border-radius: ${radius.full};
  cursor: pointer;

  color: ${colors.onSurface};
  font-size: 12px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.1em;

  transition: border-color 0.2s;

  &:hover {
    border-color: ${colors.primary};
  }

  ${props =>
    props.locked &&
    css`
      opacity: 0.6;

      &:hover {
        border-color: ${colors.outline};
      }
    `}
`;

export const StatsRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 20px;

  @media (max-width: 1199px) {
    grid-template-columns: 1fr;
  }
`;

export const StatCard = styled.div`
  position: relative;
  overflow: hidden;

  padding: 28px;

  background-color: ${colors.surfaceContainerLow};
  border-radius: ${radius.md};
`;

export const StatLabel = styled.span`
  display: block;
  margin-bottom: 14px;

  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.15em;
  color: ${colors.onSurfaceVariant};
`;

interface StatValueProps {
  tone?: 'primary' | 'secondary' | 'default';
}

export const StatValue = styled.div<StatValueProps>`
  font-size: 32px;
  font-weight: 800;
  color: ${colors.onSurface};

  ${({ tone }) =>
    tone === 'primary' &&
    css`
      color: ${colors.primary};
    `}

  ${({ tone }) =>
    tone === 'secondary' &&
    css`
      color: ${colors.secondary};
    `}

  span {
    font-size: 13px;
    font-weight: 500;
    color: ${colors.onSurfaceVariant};
    margin-left: 8px;
  }
`;

export const StatBackgroundIcon = styled.div`
  position: absolute;
  right: -8px;
  bottom: -8px;
  opacity: 0.06;
`;

export const GrowthCard = styled(StatCard)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;

export const GrowthChart = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 4px;
  height: 56px;
`;

interface GrowthBarProps {
  height: number;
  filled?: boolean;
}

export const GrowthBar = styled.div<GrowthBarProps>`
  width: 10px;
  height: ${({ height }) => height}%;
  border-radius: 2px 2px 0 0;
  background-color: ${colors.primary};
  opacity: ${({ filled }) => (filled ? 1 : 0.25)};
`;

export const TableWrapperTitle = styled.span`
  cursor: help;
`;

export const SelectWrapper = styled.div`
  min-width: 220px;
`;

export const FiltersRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`;

export const MemberCell = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`;

export const Avatar = styled.img`
  width: 46px;
  height: 46px;
  border-radius: ${radius.DEFAULT};
  object-fit: cover;
`;

export const MemberName = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: ${colors.onSurface};
  display: block;
`;

export const MemberUsername = styled.span`
  font-size: 12px;
  color: ${colors.onSurfaceVariant};
`;

interface BadgeProps {
  level: number;
}

export const LevelBadge = styled.span<BadgeProps>`
  display: inline-block;
  padding: 4px 12px;
  border-radius: ${radius.full};

  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;

  background-color: ${colors.surfaceContainerHighest};
  color: ${colors.onSurfaceVariant};

  ${({ level }) =>
    level >= 2 &&
    css`
      background-color: ${colors.primary}1a;
      color: ${colors.primary};
    `}
`;

interface StatusBadgeProps {
  status: 'active' | 'pending' | 'inactive';
}

const statusColor = {
  active: colors.primary,
  pending: colors.tertiary,
  inactive: colors.onSurfaceVariant,
};

export const StatusBadge = styled.span<StatusBadgeProps>`
  display: inline-flex;
  align-items: center;
  gap: 6px;

  padding: 4px 12px;
  border-radius: ${radius.full};

  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;

  background-color: ${({ status }) =>
    status === 'inactive'
      ? colors.surfaceContainerHighest
      : `${statusColor[status]}1a`};
  color: ${({ status }) => statusColor[status]};

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background-color: currentColor;
  }
`;

interface BillingBadgeProps {
  status: 'ok' | 'pending' | 'overdue' | 'not_informed';
}

const billingColor = {
  ok: '#32a54a',
  pending: colors.onSurfaceVariant,
  overdue: colors.primary,
  not_informed: colors.onSurfaceVariant,
};

export const BillingBadge = styled.span<BillingBadgeProps>`
  display: inline-flex;
  padding: 4px 12px;
  border-radius: ${radius.full};

  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;

  background-color: ${({ status }) =>
    status === 'not_informed'
      ? colors.surfaceContainerHighest
      : `${billingColor[status]}1a`};
  color: ${({ status }) => billingColor[status]};
`;

export const LastSession = styled.div`
  span {
    display: block;
  }

  span:first-child {
    font-size: 13px;
    font-weight: 500;
    color: ${colors.onSurface};
  }

  span:last-child {
    font-size: 10px;
    color: ${colors.onSurfaceVariant};
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }
`;

export const ActionsGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
`;

export const RowLink = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  color: ${colors.onSurfaceVariant};

  padding: 8px;
  border-radius: ${radius.DEFAULT};

  display: flex;
  align-items: center;

  transition: background-color 0.2s;

  &:hover {
    background-color: ${colors.surfaceContainerHighest};
    color: ${colors.onSurface};
  }
`;
